import { CompanyDomainTarget, POSPurchaseFlowType } from '../enums';
import { Media } from './Media';
import { Shop } from './Shop';

export interface POS {
  id: string;
  name: string;
  type: POSType;
  available_from: string;
  available_to: string;
  shop_id: string;
  // Only `id` and `name` are populated here - the POS projection builds a stub ShopModel rather
  // than loading the whole shop. Every other field is undefined at runtime.
  shop: Shop;
  seat_selection_type: POSPurchaseFlowType;
  permalink: string;
  activation_ready: boolean;
  status: POSStatus;
  color_scheme: number;
  seats_limit_enabled: boolean;
  seats_limit: number;
  disable_seat_validation: boolean;
  disable_seats_auto_assign: boolean;
  order_time_limit_enabled: boolean;
  order_time_limit: number;
  reserve_after_show_start_enabled: boolean;
  reserve_after_show_start: number;
  payment_method_ids: string[];
  // PAUL-2852: INVERSE list — a method id here disables refunds for that method at this POS;
  // null/absent = no change (every method keeps refunds enabled, the column default).
  refund_disabled_payment_method_ids?: string[];
  // PAUL-3664: methods the cash register shows on its order screen. Absent on save = none.
  order_screen_payment_method_ids?: string[];
  product_ids: string[];
  product_as_addon_ids: string[];
  /* PAUL-3461: Moderator and above only, and the key is ABSENT rather than [] for everyone else —
     GET /v1.0/pos/{id} is anonymous and a discount id doubles as a usable code. So `undefined` means
     "this caller may not see the list" and `[]` means "nothing is linked here"; never default it to []
     in a mapper, or an empty picker's save would unlink every discount on the POS. */
  discount_ids?: string[];
  available_product_relation_types: number[];
  receipt_printer_id: string;
  additionalProp1?: string;
  additionalProp2?: string;
  additionalProp3?: string;
  payment_terminal_external_id: string;
  skip_addons: boolean;
  allow_reservations: boolean;
  cart_hold_grace_enabled: boolean;
  cart_hold_grace_minutes: number;
  ticket_sales_end_offset: number;
  send_reservation_confirmation_email: boolean;
  reservation_requires_user_account: boolean;
  max_concurrent_reservations: number;
  accepts_pre_order_pickup: boolean;
  auto_print: boolean;
  // Kitchen Display only: hides the "Ready for serving" lane on the board, so Preparing advances
  // straight to Served. Switching it on also sweeps whatever is waiting in that lane to served (PAUL-3318).
  skip_ready_for_serving: boolean;
  all_areas_linked: boolean;
  // F&B Cash Register (FnbCounter) only: the register captures a seat before an order can be created.
  enable_order_to_seat: boolean;
  // F&B Cash Register (FnbCounter) only: the register shows the order overview next to the payment
  // methods on its payment step. Off by default.
  show_order_overview_during_payment: boolean;
  // Cash Register (Physical, FnbCounter) only: product tiles show the product's primary image instead of a
  // name box (PAUL-3665). The API ignores it on any other type. Off by default.
  show_product_images: boolean;
  // F&B Web only. Null = the reservation never expires; the count runs from creation, not from show start.
  fnb_reservation_expiry_minutes: number | null;
  reservation_payment_option_text_nl: string | null;
  reservation_payment_option_text_en: string | null;
  linked_area_ids: string[];
  favicon_light_id: string;
  favicon_light: Media;
  favicon_dark_id: string;
  favicon_dark: Media;
  // Google Tag Manager container the web-facing types load (PAUL-3448). Null = no tracking.
  tag_manager_container_id: string | null;
  // PAUL-3950: the company web address this point of sale sells on; null is the general address
  // (tickets.pauly.co). Must be one of the company's addresses whose target serves this type.
  company_domain_id: string | null;
  // Read-only: the hostname behind company_domain_id. Lists carry it too.
  company_domain?: string | null;
  // is_active?: boolean;
  // reservation_time_limit_enabled: boolean;
  // reservation_time_limit: number;
  // reservation_time_after_show_start_enabled: boolean;
  // reservation_time_after_show_start: number;
}

export enum POSColorScheme {
  Dark = 0,
  Light = 1,
}

export enum POSStatus {
  Draft = 0,
  Active = 1,
}

export enum POSType {
  Physical = 0,
  Online = 1,
  Subscription = 2,
  FnbCounter = 3,
  FnbWeb = 4,
  FnbKitchenDisplay = 5,
  PrepaidOnline = 6,
}

export const POSTypeTranslations: Record<POSType, string> = {
  [POSType.Physical]: 'pos_dashboard.physical',
  [POSType.Online]: 'pos_dashboard.online',
  [POSType.Subscription]: 'pos_dashboard.subscription',
  [POSType.FnbCounter]: 'pos_dashboard.fnb_counter',
  [POSType.FnbWeb]: 'pos_dashboard.fnb_web',
  [POSType.FnbKitchenDisplay]: 'pos_dashboard.fnb_kitchen_display',
  [POSType.PrepaidOnline]: 'pos_dashboard.prepaid_online',
};

/* PAUL-3950: which company web addresses a point of sale may sell on is decided by the App Service its type
   runs on. Mirrors the backend's CompanyDomainTargets.TargetOf; the POS form and the Integrations page both
   read it, so it lives here with the type rather than in either screen. */
const COMPANY_DOMAIN_TARGET_OF_POS_TYPE: Record<POSType, CompanyDomainTarget> = {
  [POSType.Online]: CompanyDomainTarget.Webshop,
  [POSType.Subscription]: CompanyDomainTarget.Webshop,
  [POSType.PrepaidOnline]: CompanyDomainTarget.Webshop,
  [POSType.FnbWeb]: CompanyDomainTarget.OrderToSeat,
  [POSType.Physical]: CompanyDomainTarget.CashRegister,
  [POSType.FnbCounter]: CompanyDomainTarget.CashRegister,
  [POSType.FnbKitchenDisplay]: CompanyDomainTarget.KitchenDisplay,
};

export const companyDomainTargetOf = (type: POSType): CompanyDomainTarget => COMPANY_DOMAIN_TARGET_OF_POS_TYPE[type];

export interface POSTerminal {
  v: string;
}
